import React from 'react';
import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import SEOMeta from '../components/SEOMeta';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-serif text-xl font-semibold text-[var(--color-deep)] mb-3 pb-2 border-b border-[var(--color-border)]">{title}</h2>
    <div className="space-y-3 text-sm text-[var(--color-muted-text)] leading-relaxed">{children}</div>
  </div>
);

export default function TerminiServizioPage() {
  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title="Termini di Servizio"
        description="Termini e Condizioni Generali di Utilizzo del portale Una Zampa In Paradiso S.r.l.s."
        url="/termini-servizio"
      />

      {/* Hero */}
      <section className="bg-[var(--color-deep)] py-14">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-2 text-[var(--color-sage)] text-xs font-bold uppercase tracking-widest mb-3">
              <FileText size={13} /> Documento Legale
            </div>
            <h1 className="font-serif text-4xl font-semibold text-white mb-2">Termini di Servizio</h1>
            <p className="text-white/60 text-sm">Condizioni Generali di Utilizzo del Portale</p>
            <p className="text-white/40 text-xs mt-2">Ultimo aggiornamento: giugno 2025</p>
          </motion.div>
        </div>
      </section>

      <section className="section-py">
        <div className="container-custom max-w-3xl">
          <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-8 shadow-sm">

            <Section title="1. Definizioni e Parti">
              <p>Ai fini dei presenti Termini di Servizio si intende per:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-[var(--color-ink)]">«Gestore»</strong> o «Società»: Una Zampa In Paradiso S.r.l.s., Via Mangiacane 11, 12011 Borgo San Dalmazzo (CN), P.IVA 04183140047.</li>
                <li><strong className="text-[var(--color-ink)]">«Portale»</strong>: il sito web raggiungibile all'indirizzo unazampainparadiso.com e relative applicazioni.</li>
                <li><strong className="text-[var(--color-ink)]">«Utente»</strong>: qualsiasi persona fisica o giuridica che accede o utilizza il Portale.</li>
                <li><strong className="text-[var(--color-ink)]">«Servizi»</strong>: l'insieme delle funzionalità offerte, inclusi cremazione animali, urne, gioielli memoriali, Giardino delle Stelle, Community.</li>
              </ul>
            </Section>

            <Section title="2. Accettazione dei Termini">
              <p>L'accesso e l'utilizzo del Portale implicano l'accettazione integrale dei presenti Termini. Chi non accetta le presenti condizioni è tenuto ad astenersi dall'utilizzo del Portale. La registrazione di un account costituisce accettazione esplicita.</p>
            </Section>

            <Section title="3. Descrizione dei Servizi">
              <p>Il Portale offre i seguenti servizi principali:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Cremazione Individuale:</strong> servizio professionale con ritiro, cerimonia e restituzione delle ceneri.</li>
                <li><strong>Shop Preventivi:</strong> richiesta di preventivo personalizzato per urne, gioielli memoriali e accessori. I preventivi non costituiscono ordini vincolanti fino alla conferma scritta.</li>
                <li><strong>Il Giardino delle Stelle:</strong> cimitero digitale per la creazione di profili memoriali degli animali.</li>
                <li><strong>Community:</strong> spazio di condivisione per amanti degli animali. I contenuti pubblicati sono sotto la responsabilità degli utenti.</li>
                <li><strong>Newsletter:</strong> comunicazioni periodiche a iscrizione volontaria.</li>
              </ul>
            </Section>

            <Section title="4. Registrazione e Account">
              <p>Per accedere ad alcuni servizi è necessario creare un account fornendo informazioni veritiere, aggiornate e complete. L'utente è responsabile della riservatezza delle proprie credenziali e di tutte le attività svolte sotto il proprio account. In caso di accesso non autorizzato, è necessario notificarlo immediatamente a <a href="mailto:info@unazampainparadiso.com" className="text-[var(--color-sage)] hover:underline">info@unazampainparadiso.com</a>.</p>
            </Section>

            <Section title="5. Obblighi dell'Utente">
              <p>L'utente si impegna a non:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Pubblicare contenuti offensivi, illeciti, diffamatori o in violazione di diritti di terzi.</li>
                <li>Utilizzare il Portale per scopi commerciali non autorizzati.</li>
                <li>Tentare di accedere a sezioni riservate senza autorizzazione.</li>
                <li>Caricare file contenenti malware o codice dannoso.</li>
                <li>Violare le norme italiane ed europee applicabili.</li>
              </ul>
              <p>Il Gestore si riserva il diritto di sospendere o chiudere account che violino queste condizioni.</p>
            </Section>

            <Section title="6. Contenuti degli Utenti">
              <p>L'utente conserva la titolarità dei contenuti caricati (foto, testi) ma concede al Gestore una licenza non esclusiva per visualizzarli nel contesto del servizio richiesto (es. memorial pubblico nel Giardino delle Stelle). Il Gestore non utilizza i contenuti per scopi diversi dall'erogazione del servizio.</p>
            </Section>

            <Section title="7. Prezzi, Preventivi e Pagamenti">
              <p>I preventivi forniti tramite il Portale sono indicativi e non vincolanti. I prezzi definitivi sono concordati direttamente con il Gestore prima dell'erogazione del servizio. Non vengono accettati pagamenti online tramite il Portale senza preventiva conferma scritta del contratto.</p>
            </Section>

            <Section title="8. Limitazione di Responsabilità">
              <p>Il Gestore non è responsabile per:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Interruzioni temporanee del servizio per manutenzione o cause di forza maggiore.</li>
                <li>Contenuti pubblicati da terzi nella Community.</li>
                <li>Danni indiretti derivanti dall'utilizzo o dalla non disponibilità del Portale.</li>
              </ul>
              <p>La responsabilità massima del Gestore è limitata al valore del servizio acquistato dall'utente.</p>
            </Section>

            <Section title="9. Proprietà Intellettuale">
              <p>Tutti i contenuti del Portale (testi, grafica, logo, codice sorgente) sono di proprietà esclusiva di Una Zampa In Paradiso S.r.l.s. o concessi in licenza. È vietata la riproduzione, distribuzione o modifica senza autorizzazione scritta.</p>
            </Section>

            <Section title="10. Legge Applicabile e Foro Competente">
              <p>I presenti Termini sono regolati dalla legge italiana. Per qualsiasi controversia derivante dall'utilizzo del Portale è competente in via esclusiva il Foro di Cuneo (CN), salvo quanto diversamente previsto dalla normativa a tutela del consumatore (D.Lgs. 206/2005).</p>
            </Section>

            <Section title="11. Modifiche ai Termini">
              <p>Il Gestore si riserva il diritto di modificare i presenti Termini in qualsiasi momento. Le modifiche entrano in vigore dalla pubblicazione sul Portale. L'utilizzo continuato del Portale successivo alla pubblicazione implica accettazione delle modifiche.</p>
            </Section>

            <Section title="12. Contatti">
              <p>
                Per qualsiasi domanda relativa ai presenti Termini:<br />
                <a href="mailto:info@unazampainparadiso.com" className="text-[var(--color-sage)] hover:underline">info@unazampainparadiso.com</a><br />
                Una Zampa In Paradiso S.r.l.s. — Via Mangiacane, 11 — 12011 Borgo San Dalmazzo (CN)
              </p>
            </Section>

          </div>
        </div>
      </section>
    </div>
  );
}
